import React, { useState, useEffect, useCallback } from "react";
import AuthService from "../../services/AuthService";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "../../services/ApiService";
import AreaChart from "../../components/AreaChart";
import BarChart from "../../components/BarChart";
import DonutChart from "../../components/DonutChart";
import Loader from "../../components/Loader";

import "./Dashboard.css";

const fmtNumber = (value) => Number(value || 0).toLocaleString();
const fmtCurrency = (value) => `AED ${Number(value || 0).toLocaleString()}`;

// Bookings-status palette (gold-forward to match theme)
const STATUS_META = [
  { key: "confirmed", label: "Confirmed", color: "#C99A3D", fields: ["confirmed_bookings", "active_bookings"] },
  { key: "pending",   label: "Pending",   color: "#E8D2A6", fields: ["pending_bookings"] },
  { key: "completed", label: "Completed", color: "#9CAF6A", fields: ["completed_bookings"] },
  { key: "cancelled", label: "Cancelled", color: "#9C8BC4", fields: ["cancelled_bookings"] },
  { key: "refunded",  label: "Refunded",  color: "#E08A76", fields: ["refunded_bookings"] },
];

const pickStat = (stats, fields) => {
  for (const f of fields) {
    if (stats?.[f] !== undefined && stats?.[f] !== null) return Number(stats[f]) || 0;
  }
  return 0;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const currentUser = AuthService.getCurrentUser();
  const adminName = currentUser?.name || currentUser?.first_name || "Admin";

  const [isLoader, setIsLoader] = useState(false);
  const [stats, setStats] = useState({});
  const [charts, setCharts] = useState({
    months: [],
    dates: [],
    month_users: [],
    month_orders: [],
    month_revenue: [],
    day_orders: [],
    day_revenue: [],
  });
  const [topServices, setTopServices] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [viewFormat, setViewFormat] = useState("monthly");

  const getDashboard = useCallback(async () => {
    try {
      setIsLoader(true);
      const [dashRes, bookingsRes] = await Promise.all([
        ApiService.request({
          method: "GET",
          url: `admin/getDashboard?view_format=${viewFormat}`,
        }),
        ApiService.request({
          method: "GET",
          url: `admin/bookings?page=1&per_page=5&sort_by=createdAt&sort_order=desc`,
        }).catch(() => null),
      ]);

      const data = dashRes.data;
      if (data.status) {
        setStats(data.data.stats || {});
        setCharts(data.data.charts || {});
        setTopServices(data.data.topServices || []);
        if (data.data.map_key) localStorage.setItem("map_key", data.data.map_key);
      } else {
        toast.error(data.message);
      }

      if (bookingsRes?.data?.status) {
        const list = bookingsRes.data.data?.records || bookingsRes.data.data?.bookings || bookingsRes.data.data || [];
        setRecentBookings(Array.isArray(list) ? list.slice(0, 5) : []);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
    }
  }, [viewFormat]);

  useEffect(() => {
    const isAuthenticated = AuthService.getCurrentUser();
    if (!isAuthenticated) {
      navigate("/admin/login");
    }
    getDashboard();
  }, [navigate, getDashboard]);

  const periodLabel = stats?.period_label || "last period";

  const totalRevenue =
    stats?.total_revenue ??
    (charts.month_revenue || []).reduce((sum, v) => sum + (Number(v) || 0), 0);

  const statCards = [
    {
      label: "Total Users",
      value: fmtNumber(stats?.job_seekers),
      change: stats?.total_customers_change,
      icon: "bi-people-fill",
      tone: "tone-amber",
      onClick: () => navigate("/admin/users"),
    },
    {
      label: "Total Providers",
      value: fmtNumber(stats?.service_providers ?? stats?.total_shops),
      change: stats?.total_shops_change,
      icon: "bi-shop",
      tone: "tone-tan",
      onClick: () => navigate("/admin/partners"),
    },
    {
      label: "Total Bookings",
      value: fmtNumber(stats?.total_bookings),
      change: stats?.total_bookings_change,
      icon: "bi-calendar2-check",
      tone: "tone-green",
      onClick: () => navigate("/admin/bookings"),
    },
    {
      label: "Total Revenue",
      value: fmtCurrency(totalRevenue),
      change: stats?.total_revenue_change,
      icon: "bi-currency-dollar",
      tone: "tone-violet",
      onClick: () => navigate("/admin/payouts"),
    },
  ];

  // Build the bookings-status breakdown from whatever real counts exist.
  const statusSegments = STATUS_META.map((m) => ({
    ...m,
    value: pickStat(stats, m.fields),
  })).filter((s) => s.value > 0);
  const statusTotal = statusSegments.reduce((sum, s) => sum + s.value, 0);

  const serviceName = (booking) => {
    const svc = (booking.services || booking.service_ids || [])[0];
    return svc?.title || svc?.name || booking.service_id?.title || "—";
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  };

  const statusBadgeClass = (status) => {
    const map = {
      pending: "status-pill status-pending",
      confirmed: "status-pill status-confirmed",
      completed: "status-pill status-completed",
      cancelled: "status-pill status-cancelled",
      refunded: "status-pill status-refunded",
    };
    return map[status?.toLowerCase()] || "status-pill status-default";
  };

  return (
    <div className="dashboard-container">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1 className="page-title">Welcome back, {adminName} 👋</h1>
          <p className="page-subtitle">Here's what's happening with Lmtkom today.</p>
        </div>
        <div className="header-right">
          <div className="view-toggle">
            {["weekly", "monthly", "yearly"].map((v) => (
              <button
                key={v}
                className={`toggle-btn ${viewFormat === v ? "active" : ""}`}
                onClick={() => setViewFormat(v)}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        {isLoader ? (
          <div className="loader-container">
            <Loader text="Loading Dashboard Data..." size="40" />
          </div>
        ) : (
          <>
            {/* Key Metrics Cards */}
            <div className="stat-grid">
              {statCards.map((card) => {
                const hasChange = card.change !== undefined && card.change !== null;
                const positive = Number(card.change) >= 0;
                return (
                  <div
                    key={card.label}
                    className="stat-card"
                    onClick={card.onClick}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="stat-card-top">
                      <div className={`stat-icon  ${card.tone}`}>
                        <i className={`bi fs-1 text-dark ${card.icon}`}></i>
                      </div>
                      <div className="stat-meta">
                        <p className="stat-label">{card.label}</p>
                        <h3 className="stat-value">{card.value}</h3>
                      </div>
                    </div>
                    {hasChange && (
                      <div className="stat-change">
                        <span className={`change-pill ${positive ? "up" : "down"}`}>
                          <i className={`bi bi-arrow-${positive ? "up" : "down"}`}></i>
                          {positive ? "+" : ""}{card.change}%
                        </span>
                        <span className="change-period">vs {periodLabel}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Charts Row: Bookings Overview + Bookings Status + Top Services */}
            <div className="dash-row">
              <div className="dash-card flex-bookings">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Bookings Overview</h3>
                </div>
                <div className="dash-card-body chart-area">
                  <AreaChart
                    labels={charts.months}
                    label="Bookings"
                    dataset={charts.month_orders}
                  />
                </div>
              </div>

              <div className="dash-card flex-status">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Bookings Status</h3>
                </div>
                <div className="dash-card-body status-body">
                  {statusTotal > 0 ? (
                    <>
                      <div className="donut-wrap">
                        <DonutChart
                          data={statusSegments.map((s) => s.value)}
                          labels={statusSegments.map((s) => s.label)}
                          colors={statusSegments.map((s) => s.color)}
                        />
                        <div className="donut-center">
                          <span className="donut-total">{fmtNumber(statusTotal)}</span>
                          <span className="donut-total-label">Total</span>
                        </div>
                      </div>
                      <ul className="status-legend">
                        {statusSegments.map((s) => {
                          const pct = ((s.value / statusTotal) * 100).toFixed(1);
                          return (
                            <li key={s.key}>
                              <span className="legend-dot" style={{ background: s.color }}></span>
                              <span className="legend-label">{s.label}</span>
                              <span className="legend-value">
                                {fmtNumber(s.value)} <span className="legend-pct">({pct}%)</span>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  ) : (
                    <div className="empty-state">No booking status data available</div>
                  )}
                </div>
              </div>

              <div className="dash-card flex-categories">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Top Services</h3>
                  <Link to="/admin/categories" className="see-all-link">View All</Link>
                </div>
                <div className="dash-card-body">
                  <ul className="top-list">
                    {topServices.length > 0 ? (
                      topServices.slice(0, 5).map((service, index) => (
                        <li key={service._id || index} className="top-list-item">
                          <span className="top-list-icon">
                            <i className="bi bi-stars"></i>
                          </span>
                          <span className="top-list-name">{service.title || "N/A"}</span>
                          <span className="top-list-count">
                            {fmtNumber(service.totalBookings)}
                          </span>
                        </li>
                      ))
                    ) : (
                      <li className="empty-state">No services data available</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom Row: Recent Bookings + Revenue Overview */}
            <div className="dash-row">
              <div className="dash-card flex-recent">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Recent Bookings</h3>
                  <Link to="/admin/bookings" className="see-all-link">View All</Link>
                </div>
                <div className="dash-card-body">
                  <div className="table-responsive">
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th>Booking ID</th>
                          <th>Customer</th>
                          <th>Service</th>
                          <th>Provider</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentBookings.length > 0 ? (
                          recentBookings.map((b, i) => (
                            <tr
                              key={b._id || i}
                              onClick={() => navigate("/admin/bookings")}
                              style={{ cursor: "pointer" }}
                            >
                              <td className="booking-code">
                                {b.booking_code || `#${b._id?.toString().slice(-6)}`}
                              </td>
                              <td>{b.user_id?.name || "N/A"}</td>
                              <td>{serviceName(b)}</td>
                              <td>{b.partner_id?.company_name || "N/A"}</td>
                              <td>{formatDate(b.event_date || b.createdAt)}</td>
                              <td>
                                <span className={statusBadgeClass(b.status)}>
                                  {(b.status || "N/A").toUpperCase()}
                                </span>
                              </td>
                              <td className="amount-cell">
                                {fmtCurrency(b.grand_total ?? b.total_amount)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="empty-state">No recent bookings</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="dash-card flex-revenue">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Revenue Overview</h3>
                </div>
                <div className="dash-card-body">
                  <div className="revenue-summary">
                    <span className="revenue-label">Total Revenue</span>
                    <h2 className="revenue-value">{fmtCurrency(totalRevenue)}</h2>
                    {stats?.total_revenue_change !== undefined && (
                      <span className={`change-pill ${Number(stats.total_revenue_change) >= 0 ? "up" : "down"}`}>
                        <i className={`bi bi-arrow-${Number(stats.total_revenue_change) >= 0 ? "up" : "down"}`}></i>
                        {Number(stats.total_revenue_change) >= 0 ? "+" : ""}{stats.total_revenue_change}%
                      </span>
                    )}
                  </div>
                  <div className="chart-area">
                    <BarChart
                      labels={charts.months}
                      label="Revenue"
                      dataset={charts.month_revenue}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
